import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, NgZone, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { UserService } from '@services/user.service';
import { IItemOperation, ItemOperationGroup, ProductionDataService } from '@shared/services/production-data.service';
import { ProductionNoteService } from '@shared/services/production-note.service';
import { ProductionTerminalService } from '@shared/services/production-terminal.service';
import { LocalStorageService } from '@shared/services/storage.service';
import { ICompletionGroupType, TerminalAdministrationService } from '@shared/services/terminalAdministration.service';
import { ISortSetting } from '@shared/services/terminaltemplateAdministration.service';
import { UUID } from 'node:crypto';
import { map, Observable, Subject, take, takeUntil } from 'rxjs';
import { ActiveWorkflowService } from '../../../shared/services/active-workflow.service';
import { MesProductionTerminalTabDialogueComponent } from '../../mes-production-terminal/mes-production-terminal-tab-dialog/mes-production-terminal-tab-dialog.component';
import { TerminalCreateReorderDialogComponent } from '../../mes-production-terminal/terminal-create-reorder-dialog/terminal-create-reorder-dialog.component';

@Component({
	selector: 'app-production-data-view',
	templateUrl: './production-data-view.component.html',
	styleUrls: ['./production-data-view.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class ProductionDataViewComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
	@ViewChild('itemViewport')
	private itemViewport!: CdkVirtualScrollViewport;

	@Input() showNonReady = false;

	private localTerminalId: UUID;
	public colWidths: number[] = [];
	private destroy$: Subject<void> = new Subject<void>();
	protected _groupColumns: {
		source: string;
		title: string;
		type: string;
		format: string;
		width?: number;
	}[] = [];

	protected _itemColumns: {
		source: string;
		title: string;
		type: string;
		format: string;
		sort: ISortSetting;
		width?: number;
	}[] = [];

	private _changingItem: boolean = false;
	protected isFirstTerminal: boolean = true;
	private isDialogOpen: boolean = false;

	constructor(
		public dataService: ProductionDataService,
		public productionTerminalService: ProductionTerminalService,
		public terminalAdministrationService: TerminalAdministrationService,
		public activeWorkFlowService: ActiveWorkflowService,
		public dialog: MatDialog,
		public noteService: ProductionNoteService,
		private ngZone: NgZone,
		private router: Router,
		private userService: UserService,
		private localStorageService: LocalStorageService,
		private changeDetector: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		const segments = this.router.url.split('/');
		const terminalId = segments[segments.length - 1] as UUID;
		this.localTerminalId = terminalId;

		this.dialog.afterOpened.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.isDialogOpen = true;
		});

		this.dialog.afterAllClosed.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.isDialogOpen = false;
		});

		// Setup the terminal when a new terminal has been selected
		this.productionTerminalService
			.onTerminalTemplateChanged()
			.pipe(takeUntil(this.destroy$))
			.subscribe((_) => {
				this.setGroupColumns();
				this.setItemColumns();
				this.updateSettings();
			});

		// The collection of groups updated, refresh all data and sort groups
		this.dataService
			.onGroupsUpdated()
			.pipe(takeUntil(this.destroy$))
			.subscribe((_) => {
				this.loadParsedItems();
			});

		// The selected item changed, scroll to item and determine if detaildata should be shown
		this.dataService
			.onCurrentItemChanged()
			.pipe(takeUntil(this.destroy$))
			.subscribe((item) => {
				this.updateSettings();
				this._changingItem = false;
				this.scrollToScannedItem(item);
			});

		// Status on an item updated, update the viewmodel
		this.dataService
			.onSomeItemUpdated()
			.pipe(takeUntil(this.destroy$))
			.subscribe((updated) => {
				this.invalidateParsedItem(updated);
				this.clearPending(`item|${updated.alias}`);
				this.clearGroupPendingWhenNoItemOperationsArePending(updated);
			});

		// New item added to a group, sort the items in the group that the item was added to
		this.dataService
			.onSomeItemCreated()
			.pipe(takeUntil(this.destroy$))
			.subscribe((item) => {
				this.invalidateParsedItem(item);
				this.clearPending(`item|${item.alias}`);
				this.dataService.groups?.filter((g) => g.items.some((op) => op.id === item.id)).forEach((g) => g.items.sort(this.compareItemOperations));
			});

		// When stationNumber changes in the data service, fetch the activeElement for that station and mark it green
		this.dataService
			.onStationNumberChanged()
			.pipe(takeUntil(this.destroy$))
			.subscribe((_) => {
				this.activeWorkFlowService.getActiveElement();
			});

		// When its the first terminal, some functionality is disabled in the HTML
		this.terminalAdministrationService
			.getFirstTerminals()
			.pipe(take(1))
			.subscribe((e) => {
				this.isFirstTerminal = e.some((t) => t.id === this.dataService.terminalId);
			});

		this.noteService.noteEvents$.pipe(takeUntil(this.destroy$)).subscribe(() => this.changeDetector.markForCheck());

		this.dataService.completionGroupType = this.productionTerminalService.terminal?.completionGroupType ?? ICompletionGroupType.Groups;
		this.refreshTerminalData();
	}

	ngAfterViewInit(): void {
		this.activeWorkFlowService.getActiveElement();
	}

	ngOnDestroy(): void {
		this.localTerminalId = null;
		this.dataService.terminalId = null;
		this.destroy$.next();
		this.destroy$.complete();
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes['showNonReady'] && !changes['showNonReady'].firstChange) {
			this.refreshTerminalData();
		}
	}

	private updateSettings() {
		if (!this.dataService?.currentItem || !this.productionTerminalService?.terminalTemplate?.data) {
			return;
		}

		this.dataService.settings = {
			getDetails: this.hasDetails(),
		};
	}

	private refreshTerminalData(): void {
		this.dataService.terminalId = this.localTerminalId;
	}

	private _parsedItems: { [id: string]: any } = {};

	private loadParsedItems(): void {
		if (!this.dataService.groups?.length) {
			this._parsedItems = {};
			return;
		}

		this.dataService.groups.sort(this.compareGroups);

		this._parsedItems = {};

		this.dataService.groups.forEach((group) => {
			group.items?.forEach((item) => {
				this._parsedItems[`${group.name}#${item.id}`] = this.createParsedItem(item);
			});
			if (this._itemColumns.length > 0 && group.items?.length) {
				group.items.sort(this.compareItemOperations);
			}
		});

		const firstGroup = this.dataService.groups[0];
		if (firstGroup) {
			this.onGroupRowClick(firstGroup);
		}
	}

	private compareGroups = (a: ItemOperationGroup, b: ItemOperationGroup): number => {
		const aHasReorder = a.items?.some((i) => i.sourceId) ?? false;
		const bHasReorder = b.items?.some((i) => i.sourceId) ?? false;
		if (aHasReorder !== bHasReorder) return aHasReorder ? -1 : 1;

		const aKey = (a.groupSortKey ?? a.name ?? 'DEFAULT').toLowerCase();
		const bKey = (b.groupSortKey ?? b.name ?? 'DEFAULT').toLowerCase();
		return aKey.localeCompare(bKey); // TODO: Compare on group sort column
	};

	private compareItemOperations = (a: IItemOperation, b: IItemOperation): number => {
		const byReorder = Number(Boolean(b.sourceId)) - Number(Boolean(a.sourceId));
		if (byReorder !== 0) return byReorder;
		return this.compare(a, b);
	};

	private compare = (a: any, b: any): number => {
		for (const col of this._itemColumns) {
			if (col.sort === 'None') continue;

			const propertyParts = col.source.split('.');
			let valA = propertyParts.reduce((acc, key) => acc?.[key], a) ?? '';
			let valB = propertyParts.reduce((acc, key) => acc?.[key], b) ?? '';

			if (/^\d+$/.test(valA)) valA = Number.parseInt(valA, 10);
			if (/^\d+$/.test(valB)) valB = Number.parseInt(valB, 10);

			if (valA < valB) return col.sort === 'Ascending' ? -1 : 1;
			if (valA > valB) return col.sort === 'Ascending' ? 1 : -1;
		}
		return 0;
	};

	startColumnResize(event: MouseEvent, listType: string, columnIndex: number) {
		event.preventDefault();

		const columns = listType === 'item' ? this._itemColumns : this._groupColumns;

		const header = (event.target as HTMLElement).closest('th') as HTMLElement;

		const startWidth = columns[columnIndex].width ?? header.offsetWidth;
		columns[columnIndex].width = startWidth;

		const startX = event.pageX;

		const mouseMove = (moveEvent: MouseEvent) => {
			const newWidth = Math.max(60, startWidth + (moveEvent.pageX - startX));
			columns[columnIndex].width = newWidth;
		};

		const mouseUp = () => {
			document.removeEventListener('mousemove', mouseMove);
			document.removeEventListener('mouseup', mouseUp);

			let key = `${this.localTerminalId}#${columnIndex}`;
			key += listType === 'item' ? '#ItemListColumns' : '#GroupListColumns';

			this.localStorageService.set(key, columns[columnIndex].width);
		};

		document.addEventListener('mousemove', mouseMove);
		document.addEventListener('mouseup', mouseUp);
	}

	private createParsedItem(item: IItemOperation): any {
		return {
			...this.flattenObject(item),
			status: this.getParsedItemStatus(item),
		};
	}

	public returnParsedItem(itemOperation: IItemOperation): any {
		const key = `${this.dataService.currentGroup?.name}#${itemOperation.id}`;
		const existing = this._parsedItems[key];
		if (existing) {
			existing.status = this.getParsedItemStatus(itemOperation);
			existing.items = itemOperation.items;
			return existing;
		}
		this._parsedItems[key] = this.createParsedItem(itemOperation);
		return this._parsedItems[key];
	}

	private invalidateParsedItem(itemOperation: IItemOperation): void {
		for (const key of Object.keys(this._parsedItems)) {
			if (key.endsWith(`#${itemOperation.id}`)) delete this._parsedItems[key];
		}
	}

	private getParsedItemStatus({ items }: IItemOperation): string {
		return `${items.filter((i) => i.ready).length} | ${items.filter((i) => i.completed).length} | ${items.filter((i) => i.reordered).length} | ${items.length}`;
	}

	public getStyle(type: string | undefined): string {
		if (!type) {
			return 'text-align-left';
		}
		const check = type.toLowerCase();
		if (check === 'number' || check === 'date') {
			return 'text-align-right';
		}
		return 'text-align-left';
	}

	private setGroupColumns() {
		this._groupColumns = this.productionTerminalService.terminalTemplate?.data?.groupList.map((grp) => ({
			source: grp.source,
			title: grp.title,
			format: grp.format,
			type: grp.dataType,
		}));

		this.setGroupColumnWidths();
	}

	private setGroupColumnWidths() {
		this._groupColumns?.forEach((column, index) => {
			const key = `${this.localTerminalId}#${index}#GroupListColumns`;
			const storedWidthStr = this.localStorageService.get(key);
			const storedWidth = Number(storedWidthStr);

			if (storedWidth && !Number.isNaN(storedWidth)) {
				column.width = storedWidth;
			}
		});
	}

	private setItemColumns() {
		const items = this.productionTerminalService.terminalTemplate?.data?.unitList ?? [];
		const arr = items?.map((item) => ({
			source: item.source,
			title: item.title,
			type: `${item.dataType}`,
			format: item.format,
			sort: item.sort,
		}));
		this._itemColumns = arr ?? [];

		this.setItemColumnWidths();
	}

	private setItemColumnWidths() {
		this._itemColumns?.forEach((column, index) => {
			const key = `${this.localTerminalId}#${index}#ItemListColumns`;
			const storedWidthStr = this.localStorageService.get(key);
			const storedWidth = Number(storedWidthStr);

			if (storedWidth && !Number.isNaN(storedWidth)) {
				column.width = storedWidth;
			}
		});
	}

	public flattenObject(obj: any, parentKey: string = '', result: any = {}): any {
		for (const key in obj) {
			const newKey = parentKey ? `${parentKey}.${key}` : key;
			const value = obj[key];
			if (typeof value === 'object' && value !== null) {
				this.flattenObject(value, newKey, result);
			} else {
				result[newKey] = value;
			}
		}
		return result;
	}

	public getGroupColumnNames(): string[] {
		return Array.from(new Set([...this._groupColumns.map((column) => column.title), 'operations']));
	}

	public getItemColumnNames(): string[] {
		return Array.from(new Set([...this._itemColumns.map((column) => column.title), 'operations']));
	}

	public hasTerminal(): boolean {
		return Boolean(this.productionTerminalService?.terminalTemplate?.data);
	}

	public hasGroups(): boolean {
		return Boolean(this.dataService.groups?.some((group) => group.name !== 'DEFAULT')) && Boolean(this.productionTerminalService?.terminalTemplate?.data?.groupList?.length);
	}

	public hasItems(): boolean {
		return Boolean(this.dataService.groups?.length);
	}

	public hasDetails(): boolean {
		return Boolean(this.dataService?.currentItem?.detailAlias) && Boolean(this.productionTerminalService?.terminalTemplate?.data?.detailView);
	}

	getSizeClass(): string {
		if (this.hasDetails()) {
			return 'detail-size';
		}
		if (this.hasGroups()) {
			return 'half-size';
		}
		return 'full-size';
	}

	preventDefaultKeyBehaviour(event: KeyboardEvent): void {
		if (this.isDialogOpen) return;

		const blockKeysAndPropagation = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);
		if (blockKeysAndPropagation.has(event.key)) {
			event.preventDefault();
			event.stopPropagation();
		}

		if (event.code === 'Space' || event.key === ' ') {
			event.preventDefault();
		}
	}

	onKeyDown(event: KeyboardEvent): void {
		if (this._changingItem || this.isDialogOpen) {
			return;
		}

		// If we have no groups on this terminal, default the selection to item list instead
		if (this._selectedPane === 'group' && !this.hasGroups()) {
			this._selectedPane = 'item';
		}

		// "Left" arrow to set selection to group pane
		// Only set group pane as selected when we actually have groups
		if (event.key === 'ArrowLeft' && this.hasGroups()) {
			this._selectedPane = 'group';
			return;
		}

		// "Right" arrow to set selection to item pane
		if (event.key === 'ArrowRight') {
			this._selectedPane = 'item';
			return;
		}

		// "Up" or "Down" arrow to move between groups or items
		if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
			if (this._selectedPane === 'item') {
				this.selectNextItem(event.key);
			} else if (this._selectedPane === 'group') {
				this.selectNextGroup(event.key);
			}
		}
	}

	scrollToScannedItem(scannedItem: IItemOperation): void {
		const items = this.dataService.currentGroup?.items || [];
		const index = items.findIndex((item) => item.id === scannedItem.id);
		if (index < 0) return;

		this.ngZone.onStable.pipe(take(1)).subscribe(() => {
			const itemHeight = 50;
			const itemCenter = (index + 0.5) * itemHeight + itemHeight;
			const viewportHeight = this.itemViewport?.getViewportSize();
			const totalHeight = items.length * itemHeight;
			const maxOffset = totalHeight - viewportHeight;

			let desiredOffset = itemCenter - viewportHeight / 2;
			desiredOffset = Math.max(0, Math.min(desiredOffset, maxOffset));

			this.itemViewport?.scrollToOffset(desiredOffset, 'instant');
		});
	}

	private _selectedPane: string | null = 'group';
	onGroupRowClick(row: ItemOperationGroup): void {
		this.dataService.currentGroup = row;
		this.dataService.currentItem = row.items[0];
		this._selectedPane = 'group';
	}

	onItemRowClick(row: IItemOperation): void {
		if (this._changingItem) return;
		this._changingItem = true;
		this.dataService.currentItem = row;
		this._selectedPane = 'item';
	}

	private selectNextItem(key: string): void {
		const items = this.dataService.currentGroup?.items;
		const currentItemIndex = items.findIndex((item) => item.id === this.dataService.currentItem?.id);

		if (currentItemIndex === -1) {
			this.onItemRowClick(items[0]);
			return;
		}

		if (key === 'ArrowDown' && currentItemIndex < items.length - 1) {
			this.onItemRowClick(items[currentItemIndex + 1]);
			return;
		}

		if (key === 'ArrowUp' && currentItemIndex > 0) {
			this.onItemRowClick(items[currentItemIndex - 1]);
		}
	}

	private selectNextGroup(key: string): void {
		const groups = this.dataService.groups;
		const currentGroupIndex = groups?.findIndex((group) => group === this.dataService.currentGroup);

		if (currentGroupIndex === -1) {
			this.onGroupRowClick(groups[0]);
			return;
		}

		if (key === 'ArrowDown' && currentGroupIndex < groups.length - 1) {
			this.onGroupRowClick(groups[currentGroupIndex + 1]);
			return;
		}

		if (key === 'ArrowUp' && currentGroupIndex > 0) {
			this.onGroupRowClick(groups[currentGroupIndex - 1]);
		}
	}

	public onGroupApprove(event: Event, row: ItemOperationGroup): void {
		event?.stopPropagation();
		const groupKey = this.setGroupPending(row);
		if (!groupKey) return;
		this.activeWorkFlowService.setActiveAlias('');
	}

	public isActiveItemRow(row: IItemOperation): boolean {
		return row.alias === this.activeWorkFlowService.activeAlias && row.items.some((i) => i.sequenceNumber === this.activeWorkFlowService.activeSequenceNumber);
	}

	canStartItem(row: IItemOperation): boolean {
		return this.approveable(row) && !this.isPending(`item|${row.alias}`) && !this.activeWorkFlowService.isActiveOnOtherStation(row.alias, row.items);
	}

	canStartGroup(row: ItemOperationGroup): boolean {
		return this.anyApproveable(row) && !this.isPending(`group|${row.name}`) && !this.activeWorkFlowService.isActiveOnOtherStation(row.name);
	}

	onStartItem(event: Event, row: IItemOperation): void {
		event?.stopPropagation();

		const batch = this.dataService.batches.find((batch) => batch.id === row.batchId);
		const firstReadyItem = [...row.items].sort((a, b) => a.sequenceNumber - b.sequenceNumber).find((it) => it.ready);
		if (!firstReadyItem || !batch) {
			return;
		}

		this.setItemActive(row, firstReadyItem.sequenceNumber);
	}

	setItemActive(item: IItemOperation, sequenceNumber: number): void {
		this.activeWorkFlowService.setItemActive(`${item.alias}%${sequenceNumber}`);
	}

	onStartGroup(event: Event, row: ItemOperationGroup): void {
		event?.stopPropagation();
		if (this.activeWorkFlowService.workFlowType === 'Group' && this.activeWorkFlowService.activeAlias) {
			const activeGroup = this.dataService.groups?.find((group) => group.name === this.activeWorkFlowService.activeAlias);
			if (activeGroup) this.setGroupPending(activeGroup);
		}
		this.activeWorkFlowService.setGroupActive(row.name);
	}

	removeActive(): void {
		this.activeWorkFlowService.clearActiveAlias();
	}

	onApprove(event: Event, row: IItemOperation): void {
		event?.stopPropagation();

		const sortedItems = [...row.items].sort((a, b) => a.sequenceNumber - b.sequenceNumber);
		const firstReadyItem = sortedItems.find((it) => it.ready);
		if (!firstReadyItem) return;

		const key = this.setPending(`item|${row.alias}`);
		if (!key) return;

		if (row.alias === this.activeWorkFlowService.activeAlias && firstReadyItem.sequenceNumber === this.activeWorkFlowService.activeSequenceNumber) {
			const nextReadyItem = sortedItems.find((it) => it.ready && it.sequenceNumber !== firstReadyItem.sequenceNumber);
			if (nextReadyItem) {
				this.setItemActive(row, nextReadyItem.sequenceNumber);
			} else {
				this.activeWorkFlowService.setActiveAlias('');
			}
		} else {
			// TODO: Remove this when Approve button is no longer visible for non-active items
			this.dataService
				.completeItemsOnItemOperation(row, [firstReadyItem], true)
				.pipe(take(1)) // Clear pending on REST error since GraphQL will not be triggered.
				.subscribe({ error: () => this.clearPending(key) });
		}
	}

	onUnapprove(event: Event, row: IItemOperation): void {
		event?.stopPropagation();
		const previousItem = row.items
			.sort((e) => e.sequenceNumber)
			.reverse()
			.find((it) => it.completed);
		if (!previousItem) return;

		const key = this.setPending(`item|${row.alias}`);
		if (!key) return;

		this.dataService
			.completeItemsOnItemOperation(row, [previousItem], false)
			.pipe(take(1)) // Clear pending on REST error since GraphQL will not be triggered.
			.subscribe({ error: () => this.clearPending(key) });
	}

	onReorder(event: Event, row: IItemOperation): void {
		event?.stopPropagation();
		this.dialog.open(TerminalCreateReorderDialogComponent, {
			width: '1200px',
			data: row,
		});
	}

	onStockReorder(event: Event, row: IItemOperation): void {
		event?.stopPropagation();
		this.dialog.open(MesProductionTerminalTabDialogueComponent, {
			width: '1200px',
			data: row,
		});
	}

	public anyApproveable(group: ItemOperationGroup): boolean {
		return group.items.some((item) => this.approveable(item));
	}

	public approveable(item: IItemOperation): boolean {
		return item.items.some((e) => e.ready);
	}

	public unapproveable(item: IItemOperation): boolean {
		return item.items.some((e) => e.completed);
	}

	public reorderable(item: IItemOperation): boolean {
		return item.items.some((e) => e.ready);
	}

	getItemClass(row: IItemOperation): string {
		const classes: string[] = [];

		const isCurrentItem = Boolean(row === this.dataService.currentItem);
		if (isCurrentItem) {
			classes.push('selected');
		}

		const isAllCompleted = Boolean(row.items.every((e) => e.completed));
		if (isAllCompleted) {
			classes.push('produced');
			return classes.join(' ');
		}

		const isAReorder = Boolean(row.sourceId);
		if (isAReorder) {
			classes.push('observation');
			return classes.join(' ');
		}

		// Item flow is cleared by the backend: the previous item is completed during the scan, so clearing here can remove the newly scanned item.
		// Group flow still clears in getGroupClass, because a group is completed asynchronously after the new element is set.
		if (this.activeWorkFlowService.workFlowType === 'Item' && row.alias === this.activeWorkFlowService.activeAlias && row.items.some((i) => i.sequenceNumber === this.activeWorkFlowService.activeSequenceNumber)) {
			if (row.items.some((item) => item.ready)) {
				classes.push('active');
			}
		} else if (this.activeWorkFlowService.workFlowType === 'Item' && this.activeWorkFlowService.isActiveOnOtherStation(row.alias, row.items)) {
			classes.push('active-other-station');
		}

		return classes.join(' ');
	}

	getGroupClass(row: ItemOperationGroup): string {
		const classes: string[] = [];

		if (row === this.dataService.currentGroup) {
			classes.push('selected');
		}

		const hasReorderItem = row.items.some((item) => item.sourceId);
		const allProduced = row.items.every((operation) => operation.items.every((item) => item.completed));

		if (hasReorderItem && !allProduced) {
			classes.push('observation');
		}

		if (allProduced) {
			classes.push('produced');
		}

		const workFlowType = this.activeWorkFlowService.workFlowType;
		const activeAlias = this.activeWorkFlowService.activeAlias;

		// Group flow: active if group has something workable, otherwise clear active.
		if (workFlowType === 'Group' && row.name === activeAlias) {
			const hasWorkable = row.items.some((operation) => operation.items.some((item) => item.ready));
			if (hasWorkable) {
				classes.push('active');
			} else {
				this.activeWorkFlowService.clearActiveAlias();
			}
		} else if (workFlowType === 'Group' && this.activeWorkFlowService.isActiveOnOtherStation(row.name)) {
			classes.push('active-other-station');
		}

		// Item flow: active if active item exists in group and can be worked on.
		if (workFlowType === 'Item') {
			const activeOperation = row.items.find((operation) => operation.alias === activeAlias && operation.items.some((i) => i.sequenceNumber === this.activeWorkFlowService.activeSequenceNumber));
			if (activeOperation) {
				const hasWorkable = activeOperation.items.some((item) => item.ready);
				if (hasWorkable) {
					classes.push('active');
				}
			} else if (row.items.some((operation) => this.activeWorkFlowService.isActiveOnOtherStation(operation.alias, operation.items))) {
				classes.push('active-other-station');
			}
		}

		return classes.join(' ');
	}

	getDetailHtml(): string {
		return this.productionTerminalService?.terminalTemplate?.data.detailView;
	}

	getDetailData(): any {
		const { account } = this.userService;
		const { currentItem, currentDetailData } = this.dataService;
		const { activeAlias, activeSequenceNumber } = this.activeWorkFlowService;

		const currentSequenceNumber = activeAlias === currentItem?.alias ? activeSequenceNumber : currentItem?.items.find((e) => e.ready)?.sequenceNumber;

		return {
			...currentItem,
			currentSequenceNumber,
			detailData: { ...currentDetailData },
			userData: {
				organisationId: account.organization.id,
				accountId: account.id,
				defaultEngine: account.defaultEngine,
			},
		};
	}

	// Pending, when API is working on approving, reordering etc. on a group or item

	private pending = new Set<string>();

	public isPending(input: string): boolean {
		return this.pending.has(input);
	}

	public setPending(input: string): string | null {
		if (this.pending.has(input)) return null;
		this.pending.add(input);
		this.changeDetector.markForCheck();
		return input;
	}

	public clearPending(input: string): void {
		if (this.pending.delete(input)) {
			this.changeDetector.markForCheck();
		}
	}

	private setGroupPending(group: ItemOperationGroup): string | null {
		const groupKey = this.setPending(`group|${group.name}`);
		for (const itemOperation of group.items) {
			if (this.approveable(itemOperation)) this.setPending(`item|${itemOperation.alias}`);
		}
		return groupKey;
	}

	private clearGroupPendingWhenNoItemOperationsArePending(itemOperation: IItemOperation): void {
		for (const group of this.dataService.groups ?? []) {
			if (!group.items.some((i) => i.id === itemOperation.id)) continue;
			if (group.items.some((i) => this.isPending(`item|${i.alias}`))) continue;
			this.clearPending(`group|${group.name}`);
		}
	}

	// Production notes

	itemHasNotes(alias: string): Observable<boolean> {
		return this.noteService.getProductionNotesByAlias(alias).pipe(map((notes) => !!notes?.length));
	}
}
